import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { trackConversion, whenUserPropertiesReady } from '@/lib/analytics';
import { getFlowIntent } from '@/lib/flowIntent';
import { readEntryMarker } from '@/lib/entryMarker';
import { catalogConversionValue } from '@/lib/catalogConversionValue';
import { useRealtimePostgresChanges } from '@/hooks/useRealtimePostgresChanges';

/**
 * Meldet den Meilenstein "erster abgeschlossener Produkt-Sync" als
 * `catalog_synced`, mit einem Konversionswert nach Kataloggroesse
 * (US-Kampagnenstart 2026-10-21). `value` ist der USD-Monatslistenpreis des
 * Kapazitaets-Bands, das der Katalog braucht (`lib/catalogConversionValue.ts`).
 *
 * WARUM EIN GESCHWISTER-HOOK UND KEINE ERWEITERUNG VON useConversionMilestones.
 * `feed_connected` bleibt damit byte-gleich und laeuft am bestehenden
 * GTM-Trigger weiter. Die beiden Meilensteine haben ausserdem eine getrennte
 * Abschalt-Logik: eigene Marke (`profiles.conversion_catalog_tracked_at`),
 * eigenes Realtime-Abo, eigene Tabelle. In einem Hook muesste das Abo des einen
 * offen bleiben, weil der andere noch aussteht.
 *
 * WARUM DAS SIGNAL DER ERSTE `completed`-JOB IST und nicht die Produktzahl beim
 * Anschluss. Beim Merchant-Center-Anschluss ist die Katalog-Groesse unbekannt:
 * die Produkte kommen erst mit dem Sync, und 127.000 Produkte brauchen Minuten.
 * Ein Wert, der beim Anschluss gezaehlt wird, waere fuer genau die grossen
 * Kataloge, die die Gebotsstrategie suchen soll, systematisch zu klein.
 * Erst `product_sync_jobs.status = 'completed'` belegt, dass die Zahl steht.
 * Ein gescheiterter Sync meldet nie; das Abo bleibt dann offen, bis ein
 * spaeterer Sync gelingt.
 *
 * WARUM DIE ZAEHLUNG UEBER DIE RPC `count_user_products` LAEUFT. Dieselbe
 * Einheit wie die Kapazitaetszaehlung in `useTotalProducts`, also dieselbe
 * Zahl, nach der App und Preisseite das Band empfehlen. Gemessen am 21.10.2026:
 * 255,6 ms auf 802.670 Produkten. Ein `count(*)` ueber die Data-API riss bei
 * Multi-MC-Konten das 8-s-Timeout (Issue #194). Die Zaehlung laeuft erst NACH
 * dem Existenzbeleg: ein Bestandskonto mit gesetzter Marke zahlt nur den einen
 * Profil-Select, ein Konto ohne abgeschlossenen Sync zusaetzlich nur ein
 * `limit(1)`.
 *
 * Ein Konto ohne eigene Produkte (etwa Mitglied eines fremden Merchant Centers)
 * meldet trotzdem, mit Wert 0 und Band `free`: der Meilenstein ist erreicht,
 * das Umsatzpotenzial nicht.
 *
 * WARUM DER REALTIME-HANDLER NUR BEI `status === 'completed'` PRUEFT. Ein
 * langer Sync schreibt je Seite und je Merge-Scheibe ein UPDATE als Heartbeat.
 * Jedes davon loeste sonst zwei Rundlaeufe aus (Profil-Select und
 * Existenzpruefung), bei 127.000 Produkten hunderte. Das Abo laeuft ueber
 * `useRealtimePostgresChanges` (die einzige erlaubte Art, einen Kanal zu
 * erzeugen) mit `filter: user_id=eq.<uid>`: anders als `merchant_centers`
 * traegt `product_sync_jobs` eine `user_id`, der Filter spart also schon
 * serverseitig jedes fremde Ereignis. Abgeschaltet wird es, sobald die Marke
 * steht (`topic === null`).
 *
 * Einmaligkeit, Schnappschuss von `flow`/`entry` beim Montieren und
 * Compare-and-Set: wie in `useConversionMilestones`, Begruendung dort. Vor dem
 * Ereignis wartet der Hook auf `whenUserPropertiesReady()`: GA4 wendet eine
 * spaeter gesetzte User-Property nicht rueckwirkend an, ohne das Warten fehlten
 * der Konversion die Enhanced-Conversions-Daten und `traffic_type` fuer
 * interne Konten.
 *
 * Jeder Fehler wird STILL geschluckt. Analytics darf nie einen Nutzerpfad
 * brechen.
 */
export function useCatalogSyncedMilestone(): void {
  const { user } = useAuth();
  const userId = user?.id ?? null;

  // Bereits behandelte User-ID plus Schnappschuss, siehe useConversionMilestones.
  const schnappschussRef = useRef<{ userId: string; flow: 'feedcheck' | 'none'; entry: 'web' | 'app' } | null>(null);
  // Kein zweiter Rundlauf, solange einer laeuft.
  const laeuftRef = useRef(false);
  // State, weil erst die Abschaltung des Abos ein Rendern braucht.
  const [abgeschlossen, setAbgeschlossen] = useState(false);

  const pruefe = useCallback(async () => {
    const schnappschuss = schnappschussRef.current;
    if (!schnappschuss) return;
    if (laeuftRef.current) return;
    laeuftRef.current = true;
    try {
      // Kurzschluss: der Normalfall ist ein Bestandskonto mit gesetzter Marke.
      const { data: profil, error: profilFehler } = await supabase
        .from('profiles')
        .select('conversion_catalog_tracked_at')
        .eq('user_id', schnappschuss.userId)
        .maybeSingle();
      if (profilFehler) return;
      const zeile = profil as unknown as { conversion_catalog_tracked_at?: string | null } | null;
      if (zeile?.conversion_catalog_tracked_at) {
        setAbgeschlossen(true);
        return;
      }

      // Existenzbeleg: mindestens ein abgeschlossener Sync. RLS scoped auf die
      // eigenen Zeilen; das user_id-Praedikat ist der zweite Riegel dazu und
      // dasselbe wie im Realtime-Filter.
      const { data: jobs, error: jobFehler } = await supabase
        .from('product_sync_jobs')
        .select('id')
        .eq('user_id', schnappschuss.userId)
        .eq('status', 'completed')
        .limit(1);
      if (jobFehler) return;
      // Noch kein abgeschlossener Sync: NICHT abschliessen, das Abo bleibt an.
      if (!jobs || jobs.length === 0) return;

      const { data: anzahl, error: zaehlFehler } = await supabase.rpc('count_user_products');
      if (zaehlFehler) return;
      const count = Number(anzahl ?? 0);
      const { value, band, currency } = catalogConversionValue(count);

      // Compare-and-Set: nur wer eine Zeile zurueckbekommt, meldet.
      const { data: geschrieben, error: schreibFehler } = await supabase
        .from('profiles')
        // Cast nur fuer die Typpruefung, das gesendete JSON ist unveraendert.
        .update({ conversion_catalog_tracked_at: new Date().toISOString() } as never)
        .eq('user_id', schnappschuss.userId)
        .is('conversion_catalog_tracked_at', null)
        .select('user_id');
      if (schreibFehler) return;
      setAbgeschlossen(true);
      if (!geschrieben || geschrieben.length === 0) return;

      await whenUserPropertiesReady();
      trackConversion('catalog_synced', {
        value,
        currency,
        catalog_products: count,
        band,
        flow: schnappschuss.flow,
        entry: schnappschuss.entry,
      });
    } catch {
      // Still schlucken, Begruendung im Kopfkommentar.
    } finally {
      laeuftRef.current = false;
    }
  }, []);

  useEffect(() => {
    if (!userId) return;
    if (schnappschussRef.current?.userId === userId) return;

    // Schnappschuss BEIM MOUNT, nicht beim Melden (siehe useConversionMilestones).
    schnappschussRef.current = {
      userId,
      flow: getFlowIntent() === 'feedcheck' ? 'feedcheck' : 'none',
      entry: readEntryMarker(),
    };
    setAbgeschlossen(false);
    void pruefe();
  }, [userId, pruefe]);

  useRealtimePostgresChanges(
    userId && !abgeschlossen ? `catalog-synced:${userId}` : null,
    { event: 'UPDATE', schema: 'public', table: 'product_sync_jobs', filter: `user_id=eq.${userId}` },
    (payload) => {
      // Nur der Abschluss zaehlt, nicht die Heartbeat-UPDATEs des laufenden Syncs.
      const neu = payload.new as { status?: string } | undefined;
      if (neu?.status === 'completed') void pruefe();
    },
  );
}
